import { api } from "@repo/convex/api";
import { emptyFeatureFlagMap, type FeatureFlagMap } from "@repo/shared";
import { useConvexAuth, useQuery } from "convex/react";
import { useMemo } from "react";
import { useOptionalOrganization } from "@/contexts/organization-context";

export interface FeatureFlagsState {
  /** Every registry key with its resolved boolean. Every key reads `false` while pending. */
  flags: FeatureFlagMap;
  status: "pending" | "ready";
}

/**
 * The caller's feature flags for the active organization.
 *
 * Pending is not denial: a menu hides an item it cannot yet vouch for, but a route gate has to
 * wait rather than answer "not found". The query is skipped until the caller is authenticated
 * and an organization is known — the same boundary `organization-context.tsx` uses for its own
 * access query — and its args carry the `orgId`, so switching organizations puts the hook back
 * into pending instead of reporting the previous organization's map as the next one's answer.
 *
 * The organization is read optionally so a surface outside the provider (an error boundary, a
 * pre-bootstrap shell) sees pending rather than throwing.
 */
export function useFeatureFlags(): FeatureFlagsState {
  const { isAuthenticated } = useConvexAuth();
  const organizationId = useOptionalOrganization()?.organizationId ?? null;

  const flags = useQuery(
    api.featureFlags.forOrganization,
    isAuthenticated && organizationId ? { orgId: organizationId } : "skip"
  );

  return useMemo(
    () =>
      flags === undefined
        ? { flags: emptyFeatureFlagMap(), status: "pending" as const }
        : { flags, status: "ready" as const },
    [flags]
  );
}
